import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'

export type Evento = Database['public']['Tables']['eventos']['Row']
type EventoInsert = Database['public']['Tables']['eventos']['Insert']
type EventoUpdate = Database['public']['Tables']['eventos']['Update']
type EventoEquipe = Database['public']['Tables']['evento_equipe']['Row']
type EventoPergunta = Database['public']['Tables']['evento_perguntas']['Row']

export interface EventoComRelacoes extends Evento {
  ministerio?: {
    id: string
    nome: string
  }
  criador?: {
    id: string
    nome: string
  }
  equipe?: EventoEquipe[]
  _count?: {
    participantes: number
    confirmados: number
  }
}

export interface EventoDetalhe extends EventoComRelacoes {
  perguntas?: EventoPergunta[]
}

// Lista eventos com filtros
export async function listarEventos(params: {
  igrejaId: string
  status?: 'ativo' | 'encerrado'
  busca?: string
}) {
  if (!supabase) throw new Error('Supabase client not initialized')

  let query = supabase
    .from('eventos')
    .select(`
      *,
      ministerio:ministerios(id, nome),
      criador:usuarios!eventos_criador_id_fkey(id, nome)
    `)
    .eq('igreja_id', params.igrejaId)
    .order('inicio_em', { ascending: false })

  // Filtrar por status (ativo = futuro, encerrado = passado)
  if (params.status === 'ativo') {
    query = query.gte('inicio_em', new Date().toISOString())
  } else if (params.status === 'encerrado') {
    query = query.lt('inicio_em', new Date().toISOString())
  }

  // Busca por título e descrição
  if (params.busca) {
    query = query.or(`titulo.ilike.%${params.busca}%,descricao.ilike.%${params.busca}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // Buscar contagem de participantes para cada evento
  const eventosComContagem = await Promise.all(
    (data || []).map(async (evento) => {
      if (!supabase) throw new Error('Supabase client not initialized')
      
      const { count: totalParticipantes } = await supabase
        .from('evento_participantes')
        .select('*', { count: 'exact', head: true })
        .eq('evento_id', evento.id)

      const { count: confirmados } = await supabase
        .from('evento_participantes')
        .select('*', { count: 'exact', head: true })
        .eq('evento_id', evento.id)
        .eq('status', 'confirmado')

      return {
        ...evento,
        _count: {
          participantes: totalParticipantes || 0,
          confirmados: confirmados || 0,
        },
      }
    })
  )

  return eventosComContagem as EventoComRelacoes[]
}

// Buscar evento por slug
export async function buscarEventoPorSlug(slug: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('eventos')
    .select(`
      *,
      ministerio:ministerios(id, nome),
      criador:usuarios!eventos_criador_id_fkey(id, nome),
      perguntas:evento_perguntas(*)
    `)
    .eq('slug', slug)
    .single()

  if (error) throw error
  return data as EventoDetalhe
}

// Buscar equipe do evento
export async function buscarEquipeEvento(eventoId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_equipe')
    .select(`
      *,
      usuario:usuarios(id, nome, email)
    `)
    .eq('evento_id', eventoId)
    .order('criador', { ascending: false })

  if (error) throw error

  // Mapear dados para o formato esperado pelo componente
  return data?.map((item: any) => ({
    id: item.usuario?.id || item.usuario_id,
    nome_exibicao: item.usuario?.nome || 'Usuário',
    avatar_url: null,
    criador: item.criador,
    permissoes: {
      editar_evento: item.editar_evento,
      confirmar_pagamento: item.confirmar_pagamento,
      excluir_evento: item.excluir_evento,
    },
  })) || []
}

// Criar evento
export async function criarEvento(evento: EventoInsert) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('eventos')
    .insert(evento)
    .select()
    .single()

  if (error) throw error
  return data as Evento
}

// Atualizar evento
export async function atualizarEvento(id: string, evento: EventoUpdate) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('eventos')
    .update(evento)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as Evento
}

// Deletar evento
export async function deletarEvento(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { error } = await supabase.from('eventos').delete().eq('id', id)

  if (error) throw error
}

// Adicionar membro à equipe
export async function adicionarMembroEquipe(params: {
  eventoId: string
  usuarioId: string
  permissoes: {
    editar_evento?: boolean
    confirmar_pagamento?: boolean
    excluir_evento?: boolean
  }
}) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_equipe')
    .insert({
      evento_id: params.eventoId,
      usuario_id: params.usuarioId,
      ...params.permissoes,
    })
    .select()
    .single()

  if (error) throw error
  return data
}

// Atualizar permissões de membro da equipe
export async function atualizarPermissoesEquipe(
  id: string,
  permissoes: {
    editar_evento?: boolean
    confirmar_pagamento?: boolean
    excluir_evento?: boolean
  }
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_equipe')
    .update(permissoes)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

// Remover membro da equipe
export async function removerMembroEquipe(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { error } = await supabase.from('evento_equipe').delete().eq('id', id)

  if (error) throw error
}

// Criar/atualizar perguntas do evento
export async function salvarPerguntasEvento(
  eventoId: string,
  perguntas: Array<{
    id?: string
    texto: string
    tipo: 'texto' | 'multipla_escolha' | 'sim_nao'
    obrigatoria: boolean
    ordem: number
  }>
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  // Deletar perguntas antigas
  await supabase.from('evento_perguntas').delete().eq('evento_id', eventoId)

  // Inserir novas perguntas
  if (perguntas.length > 0) {
    const { data, error } = await supabase
      .from('evento_perguntas')
      .insert(
        perguntas.map((p) => ({
          evento_id: eventoId,
          texto: p.texto,
          tipo: p.tipo,
          obrigatoria: p.obrigatoria,
          ordem: p.ordem,
        }))
      )
      .select()

    if (error) throw error
    return data
  }

  return []
}

// Upload de capa do evento
export async function uploadCapaEvento(file: File, igrejaId: string, eventoId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const fileExt = file.name.split('.').pop()
  const fileName = `${eventoId}-${Date.now()}.${fileExt}`
  const filePath = `${igrejaId}/${eventoId}/${fileName}`

  const { error: uploadError } = await supabase.storage
    .from('eventos-capas')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) throw uploadError

  // Obter URL pública
  const {
    data: { publicUrl },
  } = supabase.storage.from('eventos-capas').getPublicUrl(filePath)

  return publicUrl
}

// ===== FUNÇÕES PARA PARCELAS =====

// Buscar parcelas de um participante
export async function buscarParcelasParticipante(participanteId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')
  const { data, error } = await supabase
    .from('participante_parcelas')
    .select('*')
    .eq('participante_id', participanteId)
    .order('numero_parcela', { ascending: true })

  if (error) throw error
  return data
}

// Recalcular parcelas restantes após pagamento
async function recalcularParcelasRestantes(participanteId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')
  // 1. Buscar todas as parcelas do participante
  const { data: todasParcelas, error: errorParcelas } = await supabase
    .from('participante_parcelas')
    .select('*')
    .eq('participante_id', participanteId)
    .order('numero_parcela', { ascending: true })

  if (errorParcelas) throw errorParcelas
  if (!todasParcelas || todasParcelas.length === 0) return

  // 2. Buscar o valor total do evento
  const { data: pagamento, error: errorPagamento } = await supabase
    .from('participante_pagamentos')
    .select('valor')
    .eq('participante_id', participanteId)
    .single()

  if (errorPagamento) throw errorPagamento
  if (!pagamento) return

  const valorTotalEvento = Number(pagamento.valor)

  // 3. Calcular quanto já foi pago
  const valorJaPago = todasParcelas
    .filter(p => p.status === 'pago')
    .reduce((acc, p) => acc + Number(p.valor), 0)

  // 4. Calcular valor restante (total do evento - já pago)
  const valorRestante = valorTotalEvento - valorJaPago

  // 5. Contar parcelas pendentes
  const parcelasPendentes = todasParcelas.filter(p => p.status === 'pendente')

  if (parcelasPendentes.length === 0) return // Todas as parcelas já foram pagas

  // 6. Se o valor restante for zero ou negativo, marcar todas como isentas
  if (valorRestante <= 0) {
    for (const parcela of parcelasPendentes) {
      if (!supabase) throw new Error('Supabase client not initialized')
      await supabase
        .from('participante_parcelas')
        .update({
          valor: 0,
          status: 'isento',
          pago_em: new Date().toISOString(),
          atualizado_em: new Date().toISOString()
        })
        .eq('id', parcela.id)
    }
    return
  }

  // 7. Redistribuir o valor restante entre as parcelas pendentes
  const valorPorParcela = valorRestante / parcelasPendentes.length
  const valorArredondado = Math.round(valorPorParcela * 100) / 100 // Arredondar para 2 casas decimais

  // 8. Atualizar cada parcela pendente
  for (let i = 0; i < parcelasPendentes.length; i++) {
    const parcela = parcelasPendentes[i]
    let novoValor = valorArredondado

    // Na última parcela, ajustar para garantir que a soma bata exatamente
    if (i === parcelasPendentes.length - 1) {
      const somaAtualizada = valorArredondado * (parcelasPendentes.length - 1)
      novoValor = Math.round((valorRestante - somaAtualizada) * 100) / 100
    }

    // Se o novo valor for zero ou negativo, marcar como isento
    if (novoValor <= 0) {
      if (!supabase) throw new Error('Supabase client not initialized')
      await supabase
        .from('participante_parcelas')
        .update({
          valor: 0,
          status: 'isento',
          pago_em: new Date().toISOString(),
          atualizado_em: new Date().toISOString()
        })
        .eq('id', parcela.id)
    } else {
      if (!supabase) throw new Error('Supabase client not initialized')
      await supabase
        .from('participante_parcelas')
        .update({
          valor: novoValor,
          atualizado_em: new Date().toISOString()
        })
        .eq('id', parcela.id)
    }
  }
}

// Marcar parcela como paga
export async function marcarParcelaComoPaga(variables: {
  parcelaId: string
  participanteId: string
  metodo?: 'pix' | 'dinheiro' | 'cartao'
  comprovanteUrl?: string
  observacoes?: string
  dataPagamento?: string
  valor?: number
}) {
  if (!supabase) throw new Error('Supabase client not initialized')
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) throw new Error('User not authenticated')

  // Buscar o ID do usuário na tabela usuarios
  const { data: usuario } = await supabase
    .from('usuarios')
    .select('id')
    .eq('auth_user_id', userId)
    .single()

  const updateData: any = {
    status: 'pago',
    metodo: variables.metodo || null,
    pago_em: variables.dataPagamento ? new Date(variables.dataPagamento).toISOString() : new Date().toISOString(),
    comprovante_url: variables.comprovanteUrl || null,
    observacoes: variables.observacoes || null,
    registrado_por: usuario?.id || null,
    atualizado_em: new Date().toISOString(),
  }

  // Atualizar valor se fornecido
  if (variables.valor !== undefined) {
    updateData.valor = variables.valor
  }

  const { data, error } = await supabase
    .from('participante_parcelas')
    .update(updateData)
    .eq('id', variables.parcelaId)
    .select()
    .single()

  if (error) throw error

  // Recalcular parcelas restantes se o valor foi alterado
  if (variables.valor !== undefined) {
    await recalcularParcelasRestantes(variables.participanteId)
  }

  return data
}

// Gerar parcelas automaticamente
export async function gerarParcelas(variables: {
  participanteId: string
  valorTotal: number
  numeroParcelas: number
  dataInicio: Date
}) {
  if (!supabase) throw new Error('Supabase client not initialized')
  const { participanteId, valorTotal, numeroParcelas, dataInicio } = variables

  // Calcular valor de cada parcela
  const valorParcela = valorTotal / numeroParcelas

  // Criar array de parcelas
  const parcelas = []
  for (let i = 1; i <= numeroParcelas; i++) {
    const vencimento = new Date(dataInicio)
    vencimento.setMonth(vencimento.getMonth() + (i - 1))

    parcelas.push({
      participante_id: participanteId,
      numero_parcela: i,
      valor: valorParcela,
      vencimento: vencimento.toISOString().split('T')[0], // Formato YYYY-MM-DD
      status: 'pendente' as const,
    })
  }

  // Inserir todas as parcelas
  const { data, error } = await supabase
    .from('participante_parcelas')
    .insert(parcelas)
    .select()

  if (error) throw error
  return data
}
